// `createScrivenings`: one CodeMirror view over a scrivenings selection (the contract's `ScriveningsEditor`). It owns no
// documents: it reports each accepted edit per fragment — split at boundaries and balanced for hidden syntax already —
// through `onEdit`, and takes other devices' changes through `applyExternal` and `setFragments`.
import { history, historyKeymap, redo as cmRedo, standardKeymap, undo as cmUndo } from '@codemirror/commands';
import {
  Compartment, EditorSelection, EditorState, Prec, Transaction, type Extension, type StateCommand,
} from '@codemirror/state';
import { drawSelection, EditorView, keymap, type ViewUpdate } from '@codemirror/view';
import type {
  ClassifiedElement, EditorEdit, EditorFragment, ScriveningsEditor, ScriveningsOptions,
} from '@writing-suite/core';
import { clipboard, copied, selectedWords } from './clipboard.ts';
import {
  deleteBackward, deleteForward, insertLineBreak, insertNewline, insertTab, linkAtSelection, proseFormat, toggleBold, toggleItalic,
  toggleUnderline, type ProseFormat,
} from './commands.ts';
import { linkAt } from './markdown.ts';
import { dividers, structureAtoms, styler } from './decorations.ts';
import { revisionMarks, revisionMarksByFragment, revisions, setRevisionMarks, type RevisionMarkState, type RevisionMarksSpec } from './revisions.ts';
import { textEdits, visibleEdits } from './textEdits.ts';
import { editingFilter, hiddenAt } from './filter.ts';
import { Layout, type Piece } from './layout.ts';
import {
  caretLeftPending, resolvePendingSpec, screenplayElementAt, screenplayElementNameAt, setScreenplayElement, setScreenplaySong, WRITABLE_ELEMENTS,
  writableElements,
} from './screenplay.ts';
import { fragmentAt, windowAround } from './session.ts';
import {
  Exact, External, ExactCaret, isMusical, layoutField, lockedField, pendingElementField, pendingEmphasisField, piecesField, setLockedFragments,
  setPieces, setSettings, scriptFormatOf, settingsField,
} from './state.ts';

export type Theme = 'book' | 'composition';

/** The contract's options, and some more (all optional). */
export interface ScriveningsCreateOptions extends ScriveningsOptions<HTMLElement> {
  /** Type size in px (book 19; composition 19 × its scale). Sets `--ws-font-size`. */
  fontSize?: number;
  /** Line height as a multiple of the size (book 1.68, composition 1.72; Write's line-spacing setting). */
  lineHeight?: number;
  /** The text column's width in px at 19 px type (book 600, scaled with the size; composition 460/600/740). */
  measure?: number;
  /** Spell checking on the content (default on). */
  spellcheck?: boolean;
  /** The text's language, for spelling and hyphenation (the project's locale). */
  lang?: string;
  /** Called when the formatting at the caret may have changed (for the format bar). */
  onFormat?: (format: ProseFormat | null) => void;
  /** Called with the words in the selection as it changes (the footer's "12 words selected"); null once nothing is
   *  selected, or the selection holds no words. */
  onSelection?: (words: number | null) => void;
  /** Opens a link's address: ⌘-click (Ctrl-click elsewhere) on its words, as in Pages. A plain click edits them. */
  onOpenLink?: (address: string) => void;
  /** More CodeMirror extensions (comment highlights, search). */
  extensions?: Extension[];
  /** How the project's scripts are laid out: 'screen' (the default), 'multicam', 'stage-us', 'stage-uk' or 'radio'. */
  scriptFormat?: string;
  /** The project is a musical (a play's `settings.musical`): songs shown as songs, Song on Tab and ⌘7. */
  musical?: boolean;
}

/** The editor, beyond the contract. */
export interface ScriveningsHandle extends ScriveningsEditor {
  readonly view: EditorView;
  /** Runs a command (`commands.toggleItalic`, `commands.setList('decimal')`…) on the view. */
  run(command: StateCommand): boolean;
  setShowFormattingMarks(show: boolean): void;
  /** Lays the scripts out as the project's format: a screenplay, a sitcom, a US or UK play, or a radio play. */
  setScriptFormat(format: string): void;
  /** Whether the project is a musical (only a stage format's scripts show it). */
  setMusical(musical: boolean): void;
  /** The caret's element by its name in this kind of script ("Stage Direction" in a play, "Song" for a musical's
   *  song), or null outside a script. */
  screenplayElementName(): string | null;
  setTheme(theme: Theme): void;
  setReadOnly(readOnly: boolean): void;
  /** The fragments the writer has locked: edits in them are refused (`ScriveningsSession.lockedFragments`). */
  setLockedFragments(ids: readonly string[]): void;
  /** Puts the caret (or a selection) in a fragment, as the app placing it (the caret rules apply; `exact` skips them). */
  select(fragmentId: string, from: number, to?: number, options?: { exact?: boolean; scroll?: boolean }): void;
  /** The formatting at the caret, for the format bar; null in a screenplay or a divider. */
  format(): ProseFormat | null;
  /** The link the selection is in (or the caret, in its words or at their end): its fragment, the words (UTF-16 in the
   *  fragment) and its address. */
  linkAtSelection(): { fragmentId: string; from: number; to: number; address: string } | null;
  /** The caret's screenplay element, when it's in a screenplay. */
  screenplayElement(): ClassifiedElement | null;
  /** The selection as the editor's Markdown and as plain text (what a copy puts on the clipboard). */
  copySelection(): { markdown: string; plain: string } | null;
  /** The words in the selection, counted as the open text is; null with nothing (or no words) selected. */
  selectedWords(): number | null;
  undo(): boolean;
  redo(): boolean;
  /** Revision mode's marks: a diff's changes per fragment (null turns them off). */
  setRevisionMarks(spec: RevisionMarksSpec | null): void;
  /** The marks as they stand now, in document positions. */
  revisionMarks(): RevisionMarkState;
  /** The marks by fragment, in each fragment's UTF-16 offsets. */
  revisionMarksByFragment(): ReturnType<typeof revisionMarksByFragment>;
  /**
   * Replaces a fragment's whole text as the writer's own edit — one undoable step, reported through `onEdit` — made
   * as the fewest small edits that turn one text into the other, so comment anchors outside what changed hold (Fix
   * quotes, Clean up and Reject All on an open fragment). Returns false when the fragment isn't shown or the editor
   * is read-only.
   */
  replaceText(fragmentId: string, text: string, userEvent?: string): boolean;
}

const piecesOf = (fragments: EditorFragment[]): (Piece & { text: string })[] =>
  fragments.map((f) => ({ id: f.id, title: f.title, kind: f.kind, opener: f.opener ?? null, text: f.text }));

const samePiece = (a: Piece, b: Piece) => a.id === b.id && a.title === b.title && a.kind === b.kind
  && (a.opener?.label ?? null) === (b.opener?.label ?? null) && (a.opener?.title ?? null) === (b.opener?.title ?? null)
  && (a.opener?.kind ?? null) === (b.opener?.kind ?? null) && (a.opener?.number ?? null) === (b.opener?.number ?? null);

/** The smallest replacement taking `a` to `b`: the differing middle, never splitting a surrogate pair
 *  (`applyExternalText`). */
export function minimalChange(a: string, b: string): { from: number; to: number; insert: string } | null {
  if (a === b) return null;
  let prefix = 0;
  const max = Math.min(a.length, b.length);
  while (prefix < max && a.charCodeAt(prefix) === b.charCodeAt(prefix)) prefix++;
  if (prefix > 0 && prefix < a.length && (a.charCodeAt(prefix) & 0xfc00) === 0xdc00) prefix--;
  let suffix = 0;
  while (suffix < a.length - prefix && suffix < b.length - prefix && a.charCodeAt(a.length - 1 - suffix) === b.charCodeAt(b.length - 1 - suffix)) suffix++;
  if (suffix > 0 && (a.charCodeAt(a.length - suffix) & 0xfc00) === 0xdc00) suffix--;
  return { from: prefix, to: a.length - suffix, insert: b.slice(prefix, b.length - suffix) };
}

/** A transaction's changes as per-fragment edits (UTF-16, within each fragment's text), in document order: apply them
 *  in the order given (each edit's offsets are in the text the edits before it left). External changes aren't edits. */
export function editsOf(tr: Transaction): EditorEdit[] {
  if (!tr.docChanged || tr.annotation(External)) return [];
  const before = tr.startState.field(layoutField), after = tr.state.field(layoutField);
  const edits: EditorEdit[] = [];
  tr.changes.iterChanges((fromA, toA, fromB, _toB, inserted) => {
    const i = before.within(fromA, toA);
    if (i < 0) { console.error('scrivenings: an edit crossed a fragment boundary', fromA, toA); return; }
    const from = fromB - after.from[i];
    const fragmentId = before.pieces[i].id;
    const insert = inserted.toString();
    // A replacement that keeps most of what it replaces (a restyle rebuilding the markers around words, a balanced
    // edit taking in the markers at its edges) is reported as only what changed: the words keep their identity in the
    // document, so a co-writer's concurrent edit to them merges instead of the words coming back twice (W9b).
    if (toA > fromA && insert.length && toA - fromA + insert.length <= 20_000) {
      let shift = 0;
      const markupBefore = hiddenAt(tr.startState, { evenWhenShown: true }), markupAfter = hiddenAt(tr.state, { evenWhenShown: true });
      const parts = visibleEdits(tr.startState.doc.sliceString(fromA, toA), insert, (k) => !markupBefore(fromA + k), (k) => !markupAfter(fromB + k));
      for (const e of parts) {
        edits.push({ fragmentId, from: from + e.from + shift, to: from + e.to + shift, insert: e.insert });
        shift += e.insert.length - (e.to - e.from);
      }
      return;
    }
    edits.push({ fragmentId, from, to: from + (toA - fromA), insert });
  });
  return edits;
}

/** The editor's keys: emphasis, Return/Backspace/Delete by the rules, Tab, screenplay elements (⌘1–⌘6, a musical's
 *  Song ⌘7). */
export const scriveningsKeymap = keymap.of([
  { key: 'Mod-i', run: toggleItalic, preventDefault: true },
  { key: 'Mod-b', run: toggleBold, preventDefault: true },
  { key: 'Mod-u', run: (t) => { toggleUnderline(t); return true; }, preventDefault: true },
  { key: 'Enter', run: insertNewline },
  { key: 'Shift-Enter', run: insertLineBreak },
  { key: 'Backspace', run: deleteBackward },
  { key: 'Delete', run: deleteForward },
  { key: 'Tab', run: insertTab(false) },
  { key: 'Shift-Tab', run: insertTab(true) },
  // ⌘1–⌘6: the elements of the project's kind of script, in its order.
  ...WRITABLE_ELEMENTS.map((_, k) => ({ key: `Mod-${k + 1}`, run: (t: { state: EditorState; dispatch: (tr: Transaction) => void }) =>
    setScreenplayElement(writableElements(scriptFormatOf(t.state))[k])(t) })),
  { key: 'Mod-7', run: (t) => isMusical(t.state) && setScreenplaySong(t) },
]);

/** The link whose words are at a document position (in them, or at their end), in prose. */
export function linkAtPosition(state: EditorState, pos: number): { from: number; to: number; address: string } | null {
  const layout = state.field(layoutField);
  const i = layout.bodyAt(pos);
  if (i < 0 || layout.isScreenplay(i)) return null;
  const { w, base } = windowAround(state, i, pos, pos);
  const link = linkAt(w.t, pos - base);
  return link ? { from: link.from + base, to: link.to + base, address: link.address } : null;
}

/** ⌘-click (Ctrl-click elsewhere) on a link's words opens it. */
const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
export function openLinkOnModClick(open: (address: string) => void): Extension {
  return EditorView.domEventHandlers({
    mousedown(event, view) {
      if (event.button !== 0 || !(IS_MAC ? event.metaKey : event.ctrlKey)) return false;
      const pos = view.posAtCoords({ x: event.clientX, y: event.clientY });
      if (pos === null) return false;
      const link = linkAtPosition(view.state, pos);
      if (!link) return false;
      event.preventDefault();
      open(link.address);
      return true;
    },
  });
}

/** Everything but the view: the state fields, the filter, history, keys, decorations and clipboard. */
export function scriveningsExtensions(o: { fragments: EditorFragment[]; showFormattingMarks?: boolean; scriptFormat?: string; musical?: boolean } = { fragments: [] }): { doc: string; extensions: Extension[] } {
  const { layout, doc } = Layout.build(piecesOf(o.fragments));
  return {
    doc,
    extensions: [
      // Only "\n" separates lines, so a "\r" in the stored text (an import's CRLF, another device's paste) stays a
      // character and every offset is the stored text's UTF-16 offset; CodeMirror's default would fold "\r\n" into one.
      EditorState.lineSeparator.of('\n'),
      layoutField.init(() => layout),
      piecesField,
      lockedField,
      settingsField.init(() => ({ showMarks: o.showFormattingMarks ?? false, scriptFormat: o.scriptFormat ?? 'screen', musical: o.musical ?? false })),
      pendingEmphasisField,
      pendingElementField,
      editingFilter,
      history(),
      Prec.high(scriveningsKeymap),
      keymap.of([...historyKeymap, ...standardKeymap]),
      dividers,
      structureAtoms,
      // Every fragment a screenplay: the page is the script's 60 columns wide (`screenplayMeasure`).
      EditorView.editorAttributes.compute([piecesField], (state): Record<string, string> => {
        const l = state.field(layoutField);
        return l.count > 0 && l.pieces.every((p) => p.kind === 'screenplay') ? { class: 'ws-screenplay-page' } : {};
      }),
      // A play's scripts are set as the play prints (`ScriptColumns.stageUS`/`.stageUK`/`.radio`), a sitcom's as a
      // screenplay's with its own capitals and spacing; a musical's songs as songs.
      EditorView.editorAttributes.compute([settingsField], (state): Record<string, string> => {
        const format = scriptFormatOf(state);
        const classes = format === 'stage-us' ? ['ws-stage-us'] : format === 'stage-uk' ? ['ws-stage-uk']
          : format === 'multicam' ? ['ws-multicam'] : format === 'radio' ? ['ws-radio'] : [];
        if (isMusical(state)) classes.push('ws-musical');
        return classes.length ? { class: classes.join(' ') } : {};
      }),
      styler,
      clipboard,
      revisions(),
    ],
  };
}

export function createScrivenings(options: ScriveningsCreateOptions): ScriveningsHandle {
  const readOnly = new Compartment();
  const theme = new Compartment();
  let scriptFormat = options.scriptFormat ?? 'screen';
  let musical = options.musical ?? false;
  // Settings ▸ Show formatting marks, as last set: a state rebuilt for another selection keeps it (not the value at creation).
  let showMarks = options.showFormattingMarks ?? false;
  const attributes = new Compartment();
  let lastCaret: { fragmentId: string; offset: number } | null | undefined;
  // Whether the last selection reported held words: a report goes out for every selection, and once more as it empties.
  let lastSelected = false;
  let destroyed = false;

  const rootAttributes = (t: Theme) => {
    const style: string[] = [];
    if (options.fontSize) style.push(`--ws-font-size: ${options.fontSize}px`);
    if (options.lineHeight) style.push(`--ws-line-height: ${options.lineHeight}`);
    if (options.measure) style.push(`--ws-measure: ${(options.measure / 19).toFixed(3)}em`);
    return EditorView.editorAttributes.of({ class: `ws-scrivenings ws-theme-${t}`, ...(style.length ? { style: style.join('; ') } : {}) });
  };
  const contentAttributes = EditorView.contentAttributes.of({
    spellcheck: options.spellcheck === false ? 'false' : 'true',
    autocorrect: 'on',
    autocapitalize: 'sentences',
    ...(options.lang ? { lang: options.lang } : {}),
    'aria-label': 'Text',
    'aria-multiline': 'true',
  });

  const listener = EditorView.updateListener.of((u: ViewUpdate) => afterUpdate(u));

  const build = (fragments: EditorFragment[]) => {
    const base = scriveningsExtensions({ fragments, showFormattingMarks: showMarks, scriptFormat, musical });
    return EditorState.create({
      doc: base.doc,
      extensions: [
        base.extensions,
        readOnly.of(EditorState.readOnly.of(options.readOnly ?? false)),
        theme.of(rootAttributes(options.theme ?? 'book')),
        attributes.of(contentAttributes),
        drawSelection(),
        EditorView.lineWrapping,
        listener,
        options.onOpenLink ? openLinkOnModClick((address) => options.onOpenLink?.(address)) : [],
        options.extensions ?? [],
      ],
      selection: EditorSelection.cursor(base.doc.length ? 2 : 0),
    });
  };

  const view = new EditorView({ state: build(options.fragments), parent: options.parent });

  function afterUpdate(u: ViewUpdate) {
    if (destroyed) return;
    const edits: EditorEdit[] = [];
    for (const tr of u.transactions) edits.push(...editsOf(tr));
    if (edits.length) options.onEdit(edits);
    if (u.selectionSet || u.docChanged) {
      const c = caretOf();
      if (!lastCaret || !c || c.fragmentId !== lastCaret.fragmentId || c.offset !== lastCaret.offset) {
        lastCaret = c;
        options.onCaret?.(c);
      }
      options.onFormat?.(formatOf());
      if (options.onSelection && (lastSelected || !u.state.selection.main.empty)) {
        const words = u.state.selection.ranges.some((r) => !r.empty) ? selectedWords(u.state) : null;
        lastSelected = words !== null;
        options.onSelection(words);
      }
    }
    // Leaving a line with a chosen screenplay element writes it in (`ScreenplayKeys.selectionChanged`).
    if (u.selectionSet && caretLeftPending(u.state)) {
      const pending = u.state.field(pendingElementField);
      queueMicrotask(() => {
        if (destroyed || view.state.field(pendingElementField) !== pending || !caretLeftPending(view.state)) return;
        const spec = resolvePendingSpec(view.state);
        if (spec) view.dispatch(spec);
      });
    }
  }

  function caretOf() {
    const f = fragmentAt(view.state, view.state.selection.main.head);
    return f ? { fragmentId: f.fragmentId, offset: f.offset } : null;
  }

  function formatOf(): ProseFormat | null {
    const layout = view.state.field(layoutField);
    const i = layout.bodyAt(view.state.selection.main.head);
    if (i < 0 || layout.isScreenplay(i)) return null;
    return proseFormat(view.state);
  }

  const handle: ScriveningsHandle = {
    view,
    setFragments(fragments) {
      const layout = view.state.field(layoutField);
      const next = piecesOf(fragments);
      const sameIds = next.length === layout.count && next.every((f, i) => f.id === layout.pieces[i].id);
      if (sameIds) {
        const changes = [];
        for (let i = 0; i < next.length; i++) {
          const old = view.state.doc.sliceString(layout.from[i], layout.to[i]);
          const c = minimalChange(old, next[i].text);
          if (c) changes.push({ from: layout.from[i] + c.from, to: layout.from[i] + c.to, insert: c.insert });
        }
        const piecesChanged = next.some((f, i) => !samePiece(f, layout.pieces[i]));
        if (!changes.length && !piecesChanged) return;
        view.dispatch({
          changes,
          effects: piecesChanged ? setPieces.of(next.map(({ id, title, kind, opener }) => ({ id, title, kind, opener }))) : [],
          annotations: [External.of(true), Transaction.addToHistory.of(false)],
        });
        return;
      }
      // Another selection: a new state (and undo history), the caret kept in its fragment if it's still here.
      const caret = caretOf();
      const scrollTop = view.scrollDOM.scrollTop;
      view.setState(build(fragments));
      const layoutNow = view.state.field(layoutField);
      const i = caret ? layoutNow.index(caret.fragmentId) : undefined;
      if (caret && i !== undefined) {
        const pos = Math.min(layoutNow.from[i] + caret.offset, layoutNow.to[i]);
        view.dispatch({ selection: EditorSelection.cursor(pos), annotations: ExactCaret.of(true) });
        view.scrollDOM.scrollTop = scrollTop;
      }
      lastCaret = undefined;
    },
    applyExternal(fragmentId, from, to, insert) {
      const layout = view.state.field(layoutField);
      const i = layout.index(fragmentId);
      if (i === undefined) return;
      const length = layout.to[i] - layout.from[i];
      const a = Math.max(0, Math.min(from, length)), b = Math.max(a, Math.min(to, length));
      view.dispatch({
        changes: { from: layout.from[i] + a, to: layout.from[i] + b, insert },
        annotations: [External.of(true), Transaction.addToHistory.of(false)],
      });
    },
    caret: caretOf,
    selection() {
      const layout = view.state.field(layoutField);
      const r = view.state.selection.main;
      const i = layout.within(r.from, r.to);
      if (i < 0) return null;
      return { fragmentId: layout.pieces[i].id, from: r.from - layout.from[i], to: r.to - layout.from[i] };
    },
    focus() { view.focus(); },
    destroy() { destroyed = true; view.destroy(); },
    run(command) { return command(view); },
    setScriptFormat(format) {
      scriptFormat = format;
      if (scriptFormatOf(view.state) !== format) view.dispatch({ effects: setSettings.of({ scriptFormat: format }) });
    },
    setMusical(on) {
      musical = on;
      if ((view.state.field(settingsField).musical ?? false) !== on) view.dispatch({ effects: setSettings.of({ musical: on }) });
    },
    screenplayElementName() { return screenplayElementNameAt(view.state); },
    setShowFormattingMarks(show) {
      showMarks = show;
      if (view.state.field(settingsField).showMarks !== show) view.dispatch({ effects: setSettings.of({ showMarks: show }) });
    },
    setTheme(t) { view.dispatch({ effects: theme.reconfigure(rootAttributes(t)) }); },
    setReadOnly(ro) { view.dispatch({ effects: readOnly.reconfigure(EditorState.readOnly.of(ro)) }); },
    setLockedFragments(ids) { view.dispatch({ effects: setLockedFragments.of(ids) }); },
    select(fragmentId, from, to = from, o = {}) {
      const layout = view.state.field(layoutField);
      const i = layout.index(fragmentId);
      if (i === undefined) return;
      const length = layout.to[i] - layout.from[i];
      const a = layout.from[i] + Math.max(0, Math.min(from, length)), b = layout.from[i] + Math.max(0, Math.min(to, length));
      view.dispatch({ selection: EditorSelection.range(a, b), annotations: o.exact ? ExactCaret.of(true) : [], scrollIntoView: o.scroll ?? true });
    },
    format: formatOf,
    linkAtSelection() {
      const link = linkAtSelection(view.state);
      if (!link) return null;
      const layout = view.state.field(layoutField);
      const i = layout.index(link.fragmentId)!;
      return { fragmentId: link.fragmentId, from: link.from - layout.from[i], to: link.to - layout.from[i], address: link.address };
    },
    screenplayElement() { return screenplayElementAt(view.state); },
    copySelection() { return copied(view.state); },
    selectedWords() { return selectedWords(view.state); },
    undo() { return cmUndo(view); },
    redo() { return cmRedo(view); },
    setRevisionMarks(spec) { view.dispatch({ effects: setRevisionMarks.of(spec) }); },
    revisionMarks() { return revisionMarks(view.state); },
    revisionMarksByFragment() { return revisionMarksByFragment(view.state); },
    replaceText(fragmentId, text, userEvent = 'input.replace') {
      if (view.state.readOnly) return false;
      const layout = view.state.field(layoutField);
      const i = layout.index(fragmentId);
      if (i === undefined) return false;
      const body = layout.from[i];
      const old = view.state.doc.sliceString(body, layout.to[i]);
      const edits = textEdits(old, text);
      if (!edits.length) return true;
      view.dispatch({
        changes: edits.map((e) => ({ from: body + e.from, to: body + e.to, insert: e.insert })),
        annotations: Exact.of(true),
        userEvent,
      });
      return true;
    },
  };
  return handle;
}

